import { Component, inject } from '@angular/core';
import {
	LO_DIALOG_DATA,
	LoButton,
	LoDialog,
	LoDialogRef,
	LoIcon
} from 'lo-design-system';
import {
	FreezeSeatDialogData,
	FreezeSeatDialogResult
} from './freeze-seat-dialog.model';

/**
 * The three things freezing a seat does, said before it is done.
 *
 * All three are consequences the owner cannot see from the team table and
 * cannot undo by pressing the button again: access stops, the charge stops, and
 * work already in flight stops with it. The third is the one that surprises
 * people — a frozen member's outreach sequences PAUSE, so a campaign the
 * account is running goes quiet — and it is the reason this is a dialog rather
 * than a one-press action.
 */
@Component({
	standalone: true,
	selector: 'app-freeze-seat-dialog',
	imports: [LoDialog, LoButton, LoIcon],
	template: `
		<lo-dialog heading="Freeze {{ data.memberLabel }}'s seat?">
			<ul class="consequences" data-testid="freeze-consequences">
				@for (line of consequences; track line) {
					<li class="lo-type-text-md">
						<lo-icon name="small_arrow_right" />
						<span>{{ line }}</span>
					</li>
				}
			</ul>

			<p class="lo-type-text-sm note">
				Their credit balance, their mailboxes and everything they made
				stay with the account. Restoring the seat puts them back,
				charged from that moment.
			</p>

			<div loDialogActions>
				<button lo-button variant="secondary" type="button" (click)="cancel()">
					Keep the seat
				</button>
				<button
					lo-button
					type="button"
					data-testid="freeze-confirm"
					(click)="confirm()"
				>
					Freeze the seat
				</button>
			</div>
		</lo-dialog>
	`,
	styles: [
		//language=scss
		`
			.consequences {
				display: flex;
				flex-direction: column;
				gap: var(--lo-space-gap-sm);
				margin: 0;
				padding: 0;
				list-style: none;

				li {
					display: flex;
					align-items: flex-start;
					gap: var(--lo-space-gap-sm);

					lo-icon {
						color: var(--lo-color-icon-default);
					}
				}
			}

			.note {
				margin: var(--lo-space-gap-lg) 0 0;
				color: var(--lo-color-text-secondary);
			}
		`
	]
})
export class FreezeSeatDialogComponent {
	/** The three consequences, in the order the spec names them. */
	readonly consequences: string[] = [
		'They lose access to the account straight away.',
		'Your account stops being charged for their seat.',
		'Their messaging sequences pause — nothing more sends from their mailboxes until the seat is restored.'
	];

	readonly data = inject<FreezeSeatDialogData>(LO_DIALOG_DATA);
	private readonly dialogRef =
		inject<LoDialogRef<FreezeSeatDialogComponent, FreezeSeatDialogResult>>(
			LoDialogRef
		);

	confirm(): void {
		this.dialogRef.close({ confirmed: true });
	}

	cancel(): void {
		this.dialogRef.close({ confirmed: false });
	}
}
